import { useQuery } from '@tanstack/react-query';
import { getLanguage, getModule } from '../services/language-service';
import { LanguageResponse, ModuleResponse } from '../types/language.types';
import { useConfig } from 'hooks/use-config';

/**
 * Custom hook to fetch all available languages from the API
 *
 * This hook uses TanStack Query to fetch and cache the list of all available
 * languages in the system. The response is typed as LanguageResponse (array of Language objects).
 * It handles loading, error states, and caching automatically.
 *
 * @returns Query object containing:
 *  - data: Array of Language objects when available
 *  - isLoading: Boolean indicating if the request is in progress
 *  - isError: Boolean indicating if the request failed
 *  - error: Error object if the request failed
 *  - refetch: Function to manually trigger a refetch
 *
 * @example
 * const { data: languages, isLoading, error } = useAvailableLanguages();
 *
 * // Use the languages data
 * if (isLoading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage error={error} />;
 * return (
 *   <ul>
 *     {languages?.map(lang => (
 *       <li key={lang.itemId}>{lang.languageName}</li>
 *     ))}
 *   </ul>
 * );
 */
// The API returns languages in creation order, so regional variants (de-CH) land far from
// their base language. Keep each language's variants together; unknown codes go last.
const LANGUAGE_ORDER = ['en-US', 'de-DE', 'de-CH', 'fr-FR', 'fr-CH', 'it-IT', 'it-CH'];

const languageRank = (code: string) => {
  const index = LANGUAGE_ORDER.indexOf(code);
  return index === -1 ? LANGUAGE_ORDER.length : index;
};

export const useAvailableLanguages = () => {
  const { configurationSettings } = useConfig();

  return useQuery<LanguageResponse>({
    queryKey: ['getLanguages'],
    queryFn: () => getLanguage(configurationSettings),
    select: (languages) =>
      [...languages].sort((a, b) => languageRank(a.languageCode) - languageRank(b.languageCode)),
  });
};

/**
 * Custom hook to fetch all available modules from the API
 *
 * This hook uses TanStack Query to fetch and cache the list of all available
 * modules in the system. The response is typed as ModuleResponse (array of Module objects).
 * It handles loading, error states, and caching automatically.
 *
 * @returns Query object containing:
 *  - data: Array of Module objects when available
 *  - isLoading: Boolean indicating if the request is in progress
 *  - isError: Boolean indicating if the request failed
 *  - error: Error object if the request failed
 *  - refetch: Function to manually trigger a refetch
 *
 * @example
 * const { data: modules, isLoading, error } = useAvailableModules();
 *
 * // Use the modules data
 * if (isLoading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage error={error} />;
 * return (
 *   <ul>
 *     {modules?.map(module => (
 *       <li key={module.itemId}>{module.moduleName}</li>
 *     ))}
 *   </ul>
 * );
 */
export const useAvailableModules = () => {
  const { configurationSettings } = useConfig();

  return useQuery<ModuleResponse>({
    queryKey: ['getModule'],
    queryFn: () => getModule(configurationSettings),
  });
};
